import { useEffect, useState } from "react";
import { toast } from "sonner";
import { SubscribersPanel } from "@/components/SubscribersPanel";
import axios from "axios";
import { Download, Loader2, Lock } from "lucide-react";
import { Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const KEY = "herus-admin-pw";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }) : "—");
const rate = (p) => (p.opens ? `${Math.round((p.scheduled / p.opens) * 100)} %` : "—");

function Stat({ label, value, testId }) {
  return (
    <div className="border border-[#0A0A0A]/10 p-8">
      <p className="text-xs uppercase tracking-[0.2em] text-[#0A0A0A]/50">{label}</p>
      <p data-testid={testId} className="mt-3 font-serif-display text-5xl text-[#C5A059]">{value}</p>
    </div>
  );
}

function Login({ onSubmit, error, loading }) {
  const [pw, setPw] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(pw); }} className="mt-12 max-w-md flex flex-col gap-4">
      <label className="text-xs uppercase tracking-[0.2em] text-[#0A0A0A]/60">Mot de passe</label>
      <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="admin-password-input" className="bg-transparent border-b border-[#0A0A0A]/20 focus:border-[#C5A059] outline-none py-3 text-[#0A0A0A]" />
      {error && <p data-testid="admin-login-error" className="text-sm text-red-700">{error}</p>}
      <button type="submit" disabled={loading || !pw} data-testid="admin-login-submit" className="mt-4 inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-[#F9F6F0] text-xs uppercase tracking-[0.2em] px-6 py-4 hover:bg-[#C5A059] hover:text-[#0A0A0A] transition-colors duration-500 disabled:opacity-50">
        {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Accéder à l'espace privé
      </button>
    </form>
  );
}

function Dashboard({ data }) {
  return (
    <div className="mt-12">
      <div className="grid sm:grid-cols-3 gap-6">
        <Stat label="Ouvertures du calendrier" value={data.total_opens} testId="stat-total-opens" />
        <Stat label="Rendez-vous confirmés" value={data.total_scheduled} testId="stat-total-scheduled" />
        <Stat label="Taux de conversion" value={rate({ opens: data.total_opens, scheduled: data.total_scheduled })} testId="stat-conversion" />
      </div>
      <h2 className="mt-16 text-xs uppercase tracking-[0.25em] font-semibold text-[#C5A059]">Par page d'origine</h2>
      <div className="mt-6 overflow-x-auto">
        <table data-testid="stats-table" className="w-full text-sm text-left text-[#0A0A0A]">
          <thead className="text-xs uppercase tracking-[0.15em] text-[#0A0A0A]/50 border-b border-[#0A0A0A]/10">
            <tr><th className="py-4 pr-4">Page</th><th className="py-4 pr-4">Ouvertures</th><th className="py-4 pr-4">Rendez-vous</th><th className="py-4 pr-4">Conversion</th><th className="py-4">Dernière activité</th></tr>
          </thead>
          <tbody>
            {data.pages.map((p) => (
              <tr key={p.page} data-testid={`stats-row-${p.page}`} className="border-b border-[#0A0A0A]/10">
                <td className="py-4 pr-4 font-medium text-[#0A0A0A]">{p.page}</td>
                <td className="py-4 pr-4">{p.opens}</td>
                <td className="py-4 pr-4 text-[#C5A059] font-semibold">{p.scheduled}</td>
                <td className="py-4 pr-4">{rate(p)}</td>
                <td className="py-4 text-[#0A0A0A]/60">{fmt(p.last)}</td>
              </tr>
            ))}
            {!data.pages.length && <tr><td colSpan={5} className="py-8 text-[#0A0A0A]/50">Aucune activité pour le moment.</td></tr>}
          </tbody>
        </table>
      </div>
      <h2 className="mt-16 text-xs uppercase tracking-[0.25em] font-semibold text-[#C5A059]">Derniers rendez-vous confirmés</h2>
      <ul data-testid="recent-bookings" className="mt-6 divide-y divide-[#0A0A0A]/10 text-sm text-[#0A0A0A]">
        {data.recent.map((r) => (
          <li key={r.id} className="py-3 flex flex-wrap justify-between gap-2"><span>{r.page}</span><span className="text-[#0A0A0A]/60">{fmt(r.created_at)}</span></li>
        ))}
        {!data.recent.length && <li className="py-3 text-[#0A0A0A]/50">Aucun rendez-vous confirmé pour le moment.</li>}
      </ul>
      <p className="mt-8 text-xs text-[#0A0A0A]/50">Le nom et l'email des personnes sont disponibles dans votre compte Calendly (champ UTM « utm_content » = page d'origine).</p>
    </div>
  );
}

const STATUSES = [["nouveau", "Nouveau"], ["a_rappeler", "À rappeler"], ["traite", "Traité"]];
const STATUS_CLS = { nouveau: "bg-[#0A0A0A] text-[#F9F6F0]", a_rappeler: "bg-[#C5A059] text-[#0A0A0A]", traite: "bg-[#0A0A0A]/10 text-[#0A0A0A]/60" };
const statusOf = (c) => c.status || "nouveau";
const statusLabel = (c) => STATUSES.find(([k]) => k === statusOf(c))[1];

function StatusControls({ c, onChange }) {
  return (
    <div className="mt-5 flex flex-wrap gap-2">
      {STATUSES.map(([k, label]) => (
        <button
          key={k}
          onClick={() => onChange(c.id, k)}
          data-testid={`contact-status-${k}-${c.id}`}
          className={`text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${statusOf(c) === k ? `${STATUS_CLS[k]} border-transparent` : "border-[#0A0A0A]/15 text-[#0A0A0A]/60 hover:border-[#C5A059] hover:text-[#C5A059]"}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function Messages({ contacts, onStatus }) {
  const [filter, setFilter] = useState("all");
  const shown = filter === "all" ? contacts : contacts.filter((c) => statusOf(c) === filter);
  const count = (k) => contacts.filter((c) => statusOf(c) === k).length;
  return (
    <div className="mt-10">
      <div className="flex flex-wrap gap-3">
        {[["all", `Tous (${contacts.length})`], ...STATUSES.map(([k, l]) => [k, `${l} (${count(k)})`])].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`contacts-filter-${k}`} className={`text-xs uppercase tracking-[0.15em] px-4 py-2 border transition-colors ${filter === k ? "border-[#C5A059] text-[#C5A059]" : "border-[#0A0A0A]/10 text-[#0A0A0A]/50 hover:text-[#0A0A0A]"}`}>
            {l}
          </button>
        ))}
      </div>
      {!shown.length && <p className="mt-10 text-sm text-[#0A0A0A]/50">Aucun message dans cette catégorie.</p>}
      <ul data-testid="contacts-list" className="mt-8 space-y-6 text-[#0A0A0A]">
        {shown.map((c) => (
          <li key={c.id} data-testid={`contact-item-${c.id}`} className={`border p-6 lg:p-8 transition-opacity ${statusOf(c) === "traite" ? "border-[#0A0A0A]/5 opacity-60" : "border-[#0A0A0A]/10 bg-white/40"}`}>
            <div className="flex flex-wrap justify-between gap-3">
              <div>
                <p className="font-serif-display text-xl">{c.name}</p>
                <a href={`mailto:${c.email}`} className="text-sm text-[#C5A059] hover:underline">{c.email}</a>
              </div>
              <div className="text-right text-xs uppercase tracking-[0.15em] text-[#0A0A0A]/50 space-y-1">
                <span data-testid={`contact-status-badge-${c.id}`} className={`inline-block px-2 py-1 text-[10px] ${STATUS_CLS[statusOf(c)]}`}>{statusLabel(c)}</span>
                <p>{fmt(c.created_at)}</p>
                {c.organization && <p>{c.organization}</p>}
                {c.profile && <p className="text-[#C5A059]">{c.profile}</p>}
              </div>
            </div>
            <p className="mt-5 text-sm leading-relaxed whitespace-pre-line text-[#0A0A0A]/80">{c.message}</p>
            <StatusControls c={c} onChange={onStatus} />
          </li>
        ))}
      </ul>
    </div>
  );
}

async function exportExcel(data) {
  const XLSX = await import("xlsx");
  const d = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR") : "");
  const wb = XLSX.utils.book_new();
  const sheet = (rows, name, widths) => {
    const header = Object.keys(widths);
    const ws = XLSX.utils.json_to_sheet(rows, { header });
    if (!rows.length) XLSX.utils.sheet_add_aoa(ws, [header]);
    ws["!cols"] = header.map((k) => ({ wch: widths[k] }));
    XLSX.utils.book_append_sheet(wb, ws, name);
  };
  sheet(data.contacts.map((c) => ({ Date: d(c.created_at), Nom: c.name, Email: c.email, Organisation: c.organization || "", Profil: c.profile || "", Statut: statusLabel(c), Message: c.message })), "Messages", { Date: 20, Nom: 24, Email: 30, Organisation: 24, Profil: 22, Statut: 14, Message: 80 });
  sheet(data.pages.map((p) => ({ Page: p.page, Ouvertures: p.opens, "Rendez-vous": p.scheduled, Conversion: rate(p), "Dernière activité": d(p.last) })), "Réservations par page", { Page: 40, Ouvertures: 12, "Rendez-vous": 14, Conversion: 12, "Dernière activité": 20 });
  sheet(data.recent.map((r) => ({ Date: d(r.created_at), Page: r.page, "Lien Calendly": r.event_uri || "" })), "Rendez-vous confirmés", { Date: 20, Page: 40, "Lien Calendly": 60 });
  XLSX.writeFile(wb, `HERUS-export-${new Date().toISOString().slice(0, 10)}.xlsx`);
}

function Tabs({ tab, setTab, counts }) {
  const T = [["bookings", `Réservations (${counts.bookings})`], ["messages", `Messages à traiter (${counts.messages})`], ["subscribers", "Abonnés & envois"]];
  return (
    <div className="mt-12 flex gap-8 border-b border-[#0A0A0A]/10">
      {T.map(([k, label]) => (
        <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`} className={`pb-4 -mb-px text-xs uppercase tracking-[0.2em] border-b-2 transition-colors ${tab === k ? "border-[#C5A059] text-[#0A0A0A]" : "border-transparent text-[#0A0A0A]/50 hover:text-[#0A0A0A]"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

export default function AdminReservations() {
  const [tab, setTab] = useState("bookings");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (pw) => {
    setLoading(true);
    setError("");
    try {
      const headers = { "X-Admin-Password": pw };
      const stats = await axios.get(`${API}/booking-stats`, { headers });
      const contacts = await axios.get(`${API}/contacts`, { headers });
      sessionStorage.setItem(KEY, pw);
      setData({ ...stats.data, contacts: contacts.data });
    } catch (e) {
      sessionStorage.removeItem(KEY);
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : "Connexion impossible.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Espace privé | HERUS";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const pw = sessionStorage.getItem(KEY);
    if (pw) load(pw);
    return () => meta.remove();
  }, []);

  const setStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${API}/contacts/${id}`, { status }, { headers: { "X-Admin-Password": sessionStorage.getItem(KEY) } });
      setData((d) => ({ ...d, contacts: d.contacts.map((c) => (c.id === id ? res.data : c)) }));
    } catch {
      toast.error("Mise à jour impossible. Merci de réessayer.");
    }
  };

  const logout = () => { sessionStorage.removeItem(KEY); setData(null); };

  return (
    <div data-testid="admin-reservations-page" className="bg-[#F9F6F0] min-h-screen">
      <section className="pt-40 pb-24 mx-auto max-w-[1200px] px-6 lg:px-12">
        <Overline>Espace privé</Overline>
        <h1 className="mt-8 font-serif-display font-light text-4xl sm:text-5xl text-[#0A0A0A]">Réservations & messages</h1>
        {data ? (
          <>
            <button onClick={() => exportExcel(data)} data-testid="admin-export-excel" className="mt-10 inline-flex items-center gap-2 bg-[#0A0A0A] text-[#F9F6F0] text-xs uppercase tracking-[0.2em] px-6 py-4 hover:bg-[#C5A059] hover:text-[#0A0A0A] transition-colors duration-500">
              <Download size={16} /> Exporter en Excel
            </button>
            <Tabs tab={tab} setTab={setTab} counts={{ bookings: data.total_scheduled, messages: data.contacts.filter((c) => statusOf(c) !== "traite").length }} />
            {tab === "bookings" && <Dashboard data={data} />}
            {tab === "messages" && <Messages contacts={data.contacts} onStatus={setStatus} />}
            {tab === "subscribers" && <SubscribersPanel />}
            <button onClick={logout} data-testid="admin-logout" className="mt-12 text-xs uppercase tracking-[0.2em] text-[#0A0A0A]/60 hover:text-[#C5A059] border-b border-[#0A0A0A]/20 pb-1">Se déconnecter</button>
          </>
        ) : (
          <Login onSubmit={load} error={error} loading={loading} />
        )}
      </section>
    </div>
  );
}
